import { useTranslation } from "react-i18next";
import { co2eTonnesOf } from "@the-real-insight/tri-model";
import type { DinAttr } from "../administration/din276/din276Tree";

/**
 * A computed attribute (`derived` in the DIN 276 metadata) — shown, never entered. Same calculation
 * as the server's leak-check intervals (tri-model), so display and obligation always agree.
 */
export default function DerivedValue({ attr, properties }: { attr: DinAttr; properties: Record<string, any> }) {
  const { t, i18n } = useTranslation();
  if (attr.derived !== "co2e") return <span className="din276tree-mute">—</span>;
  const result = co2eTonnesOf(properties);
  if (result.co2eTonnes === null) {
    return (
      <span className="din276tree-mute" title={t("derived.computedHint", "Berechnet, nicht editierbar") as string}>
        {t(`derived.co2eMissing.${result.missing}`, {
          defaultValue: "nicht berechenbar",
          refrigerant: result.refrigerant ?? "",
        })}
      </span>
    );
  }
  const number = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(result.co2eTonnes);
  return (
    <span title={t("derived.computedHint", "Berechnet, nicht editierbar") as string}>
      <strong>{number} t CO₂e</strong>
      <span className="din276tree-mute">
        {" · "}
        {result.declared
          ? t("derived.co2eDeclared", "angegebener Wert")
          : t("derived.co2eFrom", {
              defaultValue: "berechnet aus {{refrigerant}} (GWP {{gwp}}) × {{charge}} kg",
              refrigerant: result.refrigerant,
              gwp: result.gwp,
              charge: result.chargeKg,
            })}
      </span>
    </span>
  );
}
